import { createReducerFunction, ImmerReducer } from 'immer-reducer';

/** A Slack account the CRM knows about. */
export interface SlackMember {
  slackUserId: string;
  name: string;
  displayName?: string;
  email?: string;
  avatar?: string;
}

export interface SlackLink extends SlackMember {
  userId: number;
  userName: string;
}

/** A CRM user with no Slack account behind them -- they cannot be tagged in a poll. */
export interface UnmappedUser {
  _id: number;
  name: string;
  email: string;
}

export interface SlackDirectory {
  unlinked: SlackMember[];
  links: SlackLink[];
  unmappedUsers: UnmappedUser[];
}

/**
 * A stored holiday. `date` is the nominal day and `observedDate` the one actually
 * taken off -- they differ whenever a weekend holiday is transferred to the Monday,
 * and the poll is scheduled from the observed one.
 */
export interface HolidayRecord {
  _id: number;
  name: string;
  title: string;
  date: string;
  observedDate: string;
  isCancelled: boolean;
  source: string;
}

export interface PollHoliday {
  _id: number;
  title: string;
  date: string;
  observedDate: string;
}

export interface PollSummary {
  _id: number;
  holiday?: PollHoliday;
  postedAt: number;
  cutoffAt: number;
  closedAt?: number;
  rest: number;
  work: number;
  /** Counted answers from Slack accounts nobody is linked to. */
  unmapped: number;
  /** Open polls only -- the team has moved on by the time an old one is read. */
  noAnswer?: number;
}

/** GET /vacations/holidays/status */
export interface HolidayStatus {
  calendar: {
    years: { year: number; total: number; sources: Record<string, number> }[];
    nextYear: number;
    hasNextYear: boolean;
    isNextYearDue: boolean;
    /** The first day the daily work.ua pull runs for next year. */
    autoSyncFrom: string;
    lastObservedDate?: string;
  };
  nextPoll?: {
    holiday: PollHoliday;
    postOn: string;
    isOverdue: boolean;
  };
  openPolls: PollSummary[];
  lastClosedPoll?: PollSummary;
  slack: {
    channel: string;
    botActive: boolean;
    participants: number;
    unlinked: { _id: number; name: string }[];
  };
}

export interface HolidaySyncResult {
  year: number;
  found: number;
  added: number;
}

export interface SlackState {
  directory: SlackDirectory | null;
  holidays: HolidayRecord[] | null;
  holidayStatus: HolidayStatus | null;
}

const initialState: SlackState = {
  directory: null,
  holidays: null,
  holidayStatus: null,
};

export class SlackReducer extends ImmerReducer<SlackState> {
  setDirectory(directory: SlackDirectory) {
    this.draftState.directory = directory;
  }

  setHolidays(holidays: HolidayRecord[]) {
    this.draftState.holidays = holidays;
  }

  setHolidayStatus(status: HolidayStatus) {
    this.draftState.holidayStatus = status;
  }

  // Both belong to the holidays screen. The directory does not -- the employee page's
  // picker reads it too.
  cleanHolidays() {
    this.draftState.holidays = null;
    this.draftState.holidayStatus = null;
  }

  cleanSlack() {
    this.draftState = initialState;
  }
}

export default createReducerFunction(SlackReducer, initialState);
